import { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { t } from "@/i18n-js/instance";
import { usePaywallCheckoutContext } from "@circle-react/contexts/Paywalls/paywallCheckoutContext";
import { Loader } from "@circle-react-shared/uikit/Loader";
import { Button } from "@circle-react-uikit/Button";
import { Typography } from "@circle-react-uikit/Typography";
import type { CheckoutFormValuesProps } from "../../../types/CheckoutFormValuesProps";
import { CheckoutFormField } from "../CheckoutFormField";

interface CouponFieldProps {
  applyCoupon: (couponCode?: string) => void;
}

export const CouponField = ({ applyCoupon }: CouponFieldProps) => {
  const { watch, setError } = useFormContext<CheckoutFormValuesProps>();
  const { couponPreviewMutation } = usePaywallCheckoutContext();

  const couponCode = watch("coupon_code");

  useEffect(() => {
    if (
      couponPreviewMutation.error?.body?.error_details?.code?.match(/coupon_/)
    ) {
      setError("coupon_code", {
        type: couponPreviewMutation.error?.body?.error_details?.code?.replace(
          "coupon_",
          "",
        ),
        message: couponPreviewMutation.error?.message,
      });
    }
  }, [couponPreviewMutation.error]);

  const validateCoupon = (couponCode: string) =>
    !["", undefined, null].includes(couponCode?.trim());
  const onClick = () => {
    if (validateCoupon(couponCode)) {
      applyCoupon();
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && validateCoupon(couponCode)) {
      e.preventDefault();
      applyCoupon();
    }
  };

  return (
    <CheckoutFormField
      type="text"
      name="coupon_code"
      disabled={couponPreviewMutation.isLoading}
      checkoutFormClassName="grow w-[150px] md:w-auto"
      wrapperClassName="w-full"
      onKeyDown={onKeyDown}
    >
      {couponCode && (
        <Button
          large
          type="button"
          className="grow md:grow-0"
          variant="secondary"
          disabled={couponPreviewMutation.isLoading}
          onClick={onClick}
          data-testid="checkout-form-apply-coupon"
        >
          {couponPreviewMutation.isLoading ? (
            <Loader />
          ) : (
            <Typography.LabelSm weight="medium">
              {t("paywall_checkout.form.buttons.apply_coupon")}
            </Typography.LabelSm>
          )}
        </Button>
      )}
    </CheckoutFormField>
  );
};
